import React, { useRef, useCallback, useState } from 'react';
import useAudiobook from '../hooks/useAudiobook';
import './AudiobookPanel.css';

/**
 * AudiobookPanel — hand Bishop a book and have it read aloud.
 *
 * Takes EPUB and PDF. EPUB is the better source: a PDF stores positioned
 * glyphs, so its text arrives with mid-sentence line breaks and split words
 * that you can hear in the narration, while an EPUB gives real paragraphs.
 *
 * Books are kept on a shelf (IndexedDB) with their reading position, so a book
 * is added once and picked back up where it stopped. An audiobook that forgets
 * your place is not an audiobook.
 */

const pct = (p) => `${Math.round((p || 0) * 100)}%`;

function BookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

export default function AudiobookPanel() {
  const fileInputRef = useRef(null);
  const [shelfOpen, setShelfOpen] = useState(false);
  const {
    status, bookTitle, pageCount, currentPage, progress, error, chapterName,
    loadAndRead, pause, resume, stop,
    books, openFromShelf, startOver, removeFromShelf, skipChapter,
  } = useAudiobook();

  const handleSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(epub|pdf)$/i.test(file.name)) {
      alert('Please choose an EPUB or PDF.');
      return;
    }
    setShelfOpen(false);
    loadAndRead(file);
  }, [loadAndRead]);

  const active = status !== 'idle';
  const unit = chapterName ? 'chapter' : 'page';

  return (
    <div className={`audiobook ${active ? 'active' : ''}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".epub,.pdf,application/epub+zip,application/pdf"
        onChange={handleSelect}
        style={{ display: 'none' }}
      />

      {!active && (
        <div className="audiobook-idle">
          <button
            className="audiobook-open"
            onClick={() => (books.length ? setShelfOpen((v) => !v) : fileInputRef.current?.click())}
            title="Have Bishop read a book aloud"
          >
            <BookIcon />
            <span>{books.length ? `Library (${books.length})` : 'Read a book aloud'}</span>
          </button>

          {shelfOpen && (
            <div className="audiobook-shelf">
              {books.map((b) => {
                const done = b.progress && b.progress.total
                  ? b.progress.index / b.progress.total
                  : 0;
                return (
                  <div className="shelf-item" key={b.id}>
                    <button className="shelf-main" onClick={() => { setShelfOpen(false); openFromShelf(b.id); }}>
                      <span className="shelf-title">{b.title}</span>
                      <span className="shelf-meta">
                        {b.author ? `${b.author} · ` : ''}
                        {b.kind.toUpperCase()}
                        {b.progress ? ` · ${pct(done)} read` : ' · not started'}
                        {b.progress?.chapterTitle ? ` · ${b.progress.chapterTitle}` : ''}
                      </span>
                      {b.progress ? (
                        <span className="shelf-bar"><span style={{ width: pct(done) }} /></span>
                      ) : null}
                    </button>
                    <div className="shelf-actions">
                      {b.progress && (
                        <button title="Start from the beginning" onClick={() => { setShelfOpen(false); startOver(b.id); }}>↺</button>
                      )}
                      <button title="Remove from library" onClick={() => removeFromShelf(b.id)}>×</button>
                    </div>
                  </div>
                );
              })}
              <button className="shelf-add" onClick={() => fileInputRef.current?.click()}>
                + Add a book (EPUB or PDF)
              </button>
            </div>
          )}
        </div>
      )}

      {active && (
        <div className="audiobook-player">
          <div className="audiobook-info">
            <span className="audiobook-title" title={bookTitle}>{bookTitle || 'Audiobook'}</span>
            <span className="audiobook-status">
              {status === 'loading' && 'Opening the book…'}
              {status === 'reading' && (chapterName
                ? `Reading — ${chapterName}`
                : (pageCount ? `Reading — ${unit} ${currentPage} of ${pageCount}` : 'Reading…'))}
              {status === 'paused' && (chapterName
                ? `Paused — ${chapterName}`
                : (pageCount ? `Paused — ${unit} ${currentPage} of ${pageCount}` : 'Paused'))}
              {status === 'done' && 'Finished'}
              {status === 'error' && (error || 'Something went wrong')}
            </span>
          </div>

          {(status === 'reading' || status === 'paused' || status === 'done') && (
            <div className="audiobook-progress">
              <div className="audiobook-progress-fill" style={{ width: pct(progress) }} />
            </div>
          )}

          <div className="audiobook-controls">
            {(status === 'reading' || status === 'paused') && (
              <button className="audiobook-btn" onClick={() => skipChapter(-1)} title="Previous chapter">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 5v14l-11-7z" /><rect x="4" y="5" width="2.5" height="14" rx="1" /></svg>
              </button>
            )}
            {status === 'reading' && (
              <button className="audiobook-btn" onClick={pause} title="Pause">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
              </button>
            )}
            {status === 'paused' && (
              <button className="audiobook-btn" onClick={resume} title="Resume">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5l12 7-12 7z" /></svg>
              </button>
            )}
            {(status === 'done' || status === 'error') && (
              <button className="audiobook-btn" onClick={() => fileInputRef.current?.click()} title="Read another book">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
              </button>
            )}
            {(status === 'reading' || status === 'paused') && (
              <button className="audiobook-btn" onClick={() => skipChapter(1)} title="Next chapter">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5v14l11-7z" /><rect x="17.5" y="5" width="2.5" height="14" rx="1" /></svg>
              </button>
            )}
            <button className="audiobook-btn stop" onClick={stop} title="Close (your place is saved)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1.5" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
